import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslate } from "@tolgee/react";

import { Button } from "@frontend/lyng/button";
import { TextArea } from "@frontend/lyng/forms";

const schema = z.object({
  visitNote: z.string().min(1, "clockout.requiredVisitNote"),
});

type FormValues = z.infer<typeof schema>;

type VisitNoteFormProps = {
  visitId: string;
  onSubmit: (values: FormValues) => Promise<void> | void;
  buttonText: string;
  loading?: boolean;
  placeholder?: string;
};

export const VisitNoteForm = ({
  visitId,
  onSubmit,
  buttonText,
  loading = false,
  placeholder,
}: VisitNoteFormProps) => {
  const { t } = useTranslate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      visitNote: localStorage.getItem("visitNote|" + visitId) ?? "",
    },
  });

  const handleFormSubmit = async (values: FormValues) => {
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      id="noter-save-form"
      method="POST"
      className="z-10 flex h-full grow flex-col"
    >
      <div className="flex h-full flex-1 grow flex-col px-2 pt-4">
        <Controller
          name="visitNote"
          control={control}
          render={({ field }) => (
            <div className="w-full">
              <TextArea
                {...field}
                data-testId="visit-note-text-area"
                control={control}
                name="visitNote"
                placeholder={placeholder || t("clockout.visitNotePlaceholder")}
                errorMessage={
                  errors.visitNote?.message
                    ? t(errors.visitNote.message)
                    : undefined
                }
                errorMessageIcon={true}
              />
            </div>
          )}
        />
      </div>

      <div className="fixed bottom-0 h-24 w-full grow items-center justify-center bg-main px-6">
        <Button
          type="submit"
          loading={loading}
          disabled={loading}
          text={buttonText}
          className="w-full"
        />
      </div>
    </form>
  );
};
